import React from "react";
import { Box, Typography, Tooltip, Chip, Stack } from "@mui/material";
import { Lock } from "lucide-react";
import dayjs from "dayjs";

/**
 * Reusable Prompt List Item component
 * 
 * @param {Object} props
 * @param {Object} props.prompt - The prompt object with _id, title, isGeneric, createdAt
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.isLocked - Whether the prompt is locked (personalized + no assessment)
 * @param {boolean} props.isSelected - Whether this prompt is currently selected
 * @param {boolean} props.isDisabled - Whether clicking is disabled (e.g., quota exceeded)
 * @param {boolean} props.showTypeChip - Whether to show Personalized/Generic chip
 * @param {boolean} props.showNewBadge - Whether to show "New" badge for recent prompts
 * @param {boolean} props.showSeenBadge - Whether to show "Seen" badge for viewed prompts
 * @param {boolean} props.isSeen - Whether the user has already viewed this prompt
 * @param {string} props.indicatorColor - Color for the indicator bar (default: primary.main)
 * @param {string} props.variant - "compact" for sidebar, "full" for pages
 * @param {boolean} props.isLoggedIn - Whether user is logged in (affects lock tooltip)
 */
const PromptListItem = ({
    prompt,
    onClick,
    isLocked = false,
    isSelected = false,
    isDisabled = false,
    showTypeChip = false,
    showNewBadge = false,
    showSeenBadge = false,
    isSeen = false,
    indicatorColor = "primary.main",
    variant = "compact",
    isLoggedIn = true,
}) => {
    const isPersonalized = !prompt.isGeneric;
    const isNew = showNewBadge && !isSeen && dayjs().diff(dayjs(prompt.createdAt), "day") < 7;
    const showSeen = showSeenBadge && isSeen && !isLocked;

    const handleClick = () => {
        if (!isDisabled) {
            onClick?.(prompt);
        }
    };

    // Compact variant (sidebar)
    if (variant === "compact") {
        return (
            <Stack
                direction="row"
                onClick={handleClick}
                sx={{
                    cursor: isDisabled ? "not-allowed" : isLocked ? "not-allowed" : "pointer",
                    px: 1.5,
                    py: 1.25,
                    borderRadius: "12px",
                    bgcolor: isSelected ? "rgba(139, 92, 246, 0.08)" : "transparent",
                    opacity: isDisabled ? 0.5 : isLocked ? 0.65 : 1,
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    "&:hover": {
                        bgcolor: !isSelected && !isLocked && !isDisabled ? "rgba(0,0,0,0.03)" : undefined,
                        transform: !isDisabled && !isLocked ? "translateX(4px)" : undefined,
                    },
                    "&::before": isSelected ? {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "20%",
                        bottom: "20%",
                        width: "3px",
                        borderRadius: "0 4px 4px 0",
                        bgcolor: indicatorColor,
                    } : {},
                }}
                spacing={1.5}
                alignItems="center"
            >
                {/* Title with tooltip */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Tooltip title={prompt.title} arrow enterDelay={500}>
                        <Typography
                            sx={{
                                fontSize: "0.8rem",
                                fontWeight: isSelected ? 700 : 500,
                                color: isSelected ? "primary.main" : isLocked ? "text.secondary" : "text.primary",
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                WebkitLineClamp: 2,
                                lineHeight: 1.4,
                                transition: "color 0.2s ease",
                            }}
                        >
                            {prompt.title}
                        </Typography>
                    </Tooltip>
                </Box>

                {/* Lock icon for personalized prompts */}
                {isLocked && (
                    <Tooltip
                        title={isLoggedIn ? "Complete assessment to unlock" : "Login and complete assessment to unlock"}
                        arrow
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)",
                                flexShrink: 0,
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                        >
                            <Lock size={11} color="#fff" />
                        </Box>
                    </Tooltip>
                )}

                {/* New indicator */}
                {isNew && !isLocked && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                            color: "white",
                            flexShrink: 0,
                            fontSize: "10px",
                            fontWeight: 800,
                            boxShadow: "0 2px 4px rgba(217, 119, 6, 0.3)",
                        }}
                    >
                        ✦
                    </Box>
                )}

                {showSeen && (
                    <Chip
                        label="Seen"
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: "9px",
                            fontWeight: 700,
                            bgcolor: "rgba(59, 130, 246, 0.1)",
                            color: "#3B82F6",
                            flexShrink: 0,
                        }}
                    />
                )}
            </Stack>
        );
    }

    // Full variant (pages)
    return (
        <Stack
            direction="row"
            onClick={handleClick}
            sx={{
                cursor: isDisabled ? "not-allowed" : isLocked ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                py: { xs: 1.5, sm: 2 },
                px: { xs: 2, sm: 2.5 },
                mb: { xs: 1, sm: 1.5 },
                borderRadius: { xs: "12px", sm: "16px" },
                minHeight: { xs: 56, sm: "auto" },
                bgcolor: isSelected ? "rgba(139, 92, 246, 0.04)" : "background.paper",
                opacity: isDisabled ? 0.5 : isLocked ? 0.75 : 1,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                border: "1px solid",
                borderColor: isSelected ? "primary.main" : "rgba(0,0,0,0.04)",
                boxShadow: isSelected ? "0 4px 20px rgba(139, 92, 246, 0.08)" : "0 2px 4px rgba(0,0,0,0.02)",
                "&:hover": {
                    bgcolor: !isDisabled && !isLocked ? (isSelected ? "rgba(139, 92, 246, 0.06)" : "grey.50") : undefined,
                    borderColor: !isDisabled && !isLocked ? (isSelected ? "primary.main" : "rgba(0,0,0,0.1)") : undefined,
                    transform: !isDisabled && !isLocked ? "translateY(-2px)" : undefined,
                    boxShadow: !isDisabled && !isLocked ? "0 8px 24px rgba(0,0,0,0.06)" : undefined,
                },
            }}
            spacing={{ xs: 1.5, sm: 2 }}
        >
            {/* Indicator bar */}
            <Box
                sx={{
                    width: 4,
                    height: { xs: 36, sm: 40 },
                    borderRadius: "4px",
                    bgcolor: isLocked ? "grey.300" : indicatorColor,
                    flexShrink: 0,
                    transition: "all 0.3s ease",
                    transform: isSelected ? "scaleY(1.1)" : "scaleY(1)",
                }}
            />

            {/* Title with tooltip */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
                        fontWeight: isSelected ? 700 : 500,
                        color: isLocked ? "text.secondary" : "text.primary",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: { xs: 1.45, sm: 1.5 },
                    }}
                >
                    {prompt.title}
                </Typography>
            </Box>

            {/* Type chip */}
            {showTypeChip && (
                <Box
                    sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "20px",
                        fontSize: "10px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        bgcolor: isPersonalized ? "rgba(139, 92, 246, 0.1)" : "rgba(0,0,0,0.05)",
                        color: isPersonalized ? "primary.main" : "text.secondary",
                        flexShrink: 0,
                    }}
                >
                    {isPersonalized ? "Personalized" : "Generic"}
                </Box>
            )}

            {/* New indicator */}
            {isNew && !isLocked && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 1.25,
                        py: 0.5,
                        borderRadius: "8px",
                        background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                        color: "white",
                        flexShrink: 0,
                        fontSize: "10px",
                        fontWeight: 800,
                        boxShadow: "0 2px 8px rgba(217, 119, 6, 0.3)",
                    }}
                >
                    NEW
                </Box>
            )}

            {showSeen && (
                <Chip
                    label="Seen"
                    size="small"
                    sx={{
                        height: { xs: 26, sm: 24 },
                        fontSize: { xs: "11px", sm: "10px" },
                        fontWeight: 700,
                        bgcolor: "rgba(59, 130, 246, 0.1)",
                        color: "#3B82F6",
                        flexShrink: 0,
                        "& .MuiChip-label": {
                            px: { xs: 1, sm: 0.75 },
                        },
                    }}
                />
            )}

            {/* Lock icon for personalized prompts */}
            {isLocked && (
                <Tooltip
                    title={isLoggedIn ? "Complete assessment to unlock" : "Login and complete assessment to unlock"}
                    arrow
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)",
                            flexShrink: 0,
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                        }}
                    >
                        <Lock size={14} color="#fff" />
                    </Box>
                </Tooltip>
            )}
        </Stack>
    );
};

export default PromptListItem;

